import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import LenderSimulator from "@/components/LenderSimulator";
import { CountUp, ScrollReveal } from "@/components/animations";
import { DURATION, EASING } from "@/lib/motionSystem";
import { TIER_COLORS } from "@/lib/tiers";
import { cohortLabel } from "@/lib/format";
import DecisionVerification from "@/components/DecisionVerification";
import {
    GraduationCap,
    CreditCard,
    Code2,
    Share2,
    Layers,
    Download,
    AlertCircle,
    Brain,
    Users,
    Globe,
    TrendingUp,
    Building,
    CheckCircle2,
    TriangleAlert,
    Clock3,
    Database,
} from "lucide-react";

const SIGNAL_META = {
    academic: { label: "Academic Intelligence", color: "#3b82f6", icon: GraduationCap },
    fee_payment: { label: "Fee Payment History", color: "#06b6d4", icon: CreditCard },
    developer_professional: { label: "Developer / Professional", color: "#8b5cf6", icon: Code2 },
    social_creator: { label: "Social / Creator", color: "#f59e0b", icon: Share2 },
    consumption: { label: "Consumption Intelligence", color: "#fb923c", icon: TrendingUp },
    coverage_consistency: { label: "Coverage & Consistency", color: "#10b981", icon: Layers },
};

const SOURCE_LABELS = {
    oauth: "OAuth Verified",
    verified_system: "System Verified",
    uploaded_document: "Uploaded Document",
    url_ingest: "URL Extracted",
    user_declared: "Self-declared",
    manual: "Self-declared",
    inferred: "Model Inferred",
};

const DIRECT_TYPES = new Set(["oauth", "verified_system", "uploaded_document", "url_ingest", "digilocker", "account_aggregator"]);

function titleCase(value = "") {
    return String(value)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value) {
    if (!value) return "N/A";
    try {
        return new Date(value).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" });
    } catch {
        return "N/A";
    }
}

function toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function getUnderwritingView(score, confidence) {
    if (score >= 760 && confidence >= 0.82) {
        return {
            label: "Preferred Approval",
            summary: "High-quality profile with strong repayment and behavior signals. Eligible for favorable terms with standard controls.",
            risk: "Low Risk",
            cls: "bg-emerald-50 border-emerald-200 text-emerald-700",
            icon: CheckCircle2,
        };
    }
    if (score >= 640 && confidence >= 0.7) {
        return {
            label: "Conditional Approval",
            summary: "Viable profile with moderate variance. Recommend step-up limits with usage review checkpoints.",
            risk: "Moderate Risk",
            cls: "bg-amber-50 border-amber-200 text-amber-700",
            icon: Clock3,
        };
    }
    return {
        label: "Starter Exposure",
        summary: "Elevated uncertainty and weaker foundational signals. Recommend low initial limits and close monitoring.",
        risk: "Elevated Risk",
        cls: "bg-rose-50 border-rose-200 text-rose-700",
        icon: TriangleAlert,
    };
}

function ExecutiveCard({ title, value, subtitle, accent = "#334155", mono = false, suffix = "", testId }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={testId}>
            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{title}</p>
            <p
                className={`${mono ? "font-mono" : ""} text-2xl font-bold tracking-tight`}
                style={{ color: accent }}
            >
                <CountUp target={toNumber(value)} duration={1.1} />
                {suffix}
            </p>
            <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
        </div>
    );
}

function Section({ title, icon: Icon, children }) {
    return (
        <section
            className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
            data-testid={`lender-section-${title.toLowerCase().replace(/\s/g, "-")}`}
        >
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
                <Icon size={16} className="text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">{title}</h3>
            </div>
            {children}
        </section>
    );
}

function DataRow({ label, value, highlight = false }) {
    return (
        <div className="flex items-start justify-between gap-3 py-1.5 text-sm min-w-0">
            <span className="text-slate-500">{label}</span>
            <span className={`font-medium text-right min-w-0 break-words ${highlight ? "text-slate-900" : "text-slate-700"}`}>{value || "—"}</span>
        </div>
    );
}

export default function LenderReport() {
    const { id } = useParams();
    const isDemo = id?.startsWith("demo-");
    const demoName = isDemo ? id.replace("demo-", "") : null;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let res;
                if (isDemo) res = await api.get(`/api/score/demo/${demoName}`);
                else res = await api.get(`/api/score/${id}`);
                setData(res.data);
            } catch (err) {
                setError(err.response?.data?.detail || "Failed to load");
            }
            setLoading(false);
        };
        fetchData();
    }, [demoName, id, isDemo]);

    const signalRows = useMemo(
        () =>
            Object.entries(data?.signal_scores || {})
                .map(([key, val]) => {
                    const score = toNumber(val?.score);
                    const weight = toNumber(val?.weight);
                    return {
                        key,
                        label: SIGNAL_META[key]?.label || titleCase(key),
                        color: SIGNAL_META[key]?.color || "#64748b",
                        score,
                        weight,
                        weightedContribution: Math.round(score * weight),
                        evidenceTypes: Array.isArray(val?.evidence_types) ? val.evidence_types : [],
                    };
                })
                .sort((a, b) => b.weightedContribution - a.weightedContribution),
        [data?.signal_scores],
    );

    const evidenceBreakdown = useMemo(() => {
        const direct = signalRows.reduce(
            (count, row) => count + row.evidenceTypes.filter((type) => DIRECT_TYPES.has(type)).length,
            0,
        );
        const inferred = signalRows.reduce(
            (count, row) => count + row.evidenceTypes.filter((type) => !DIRECT_TYPES.has(type)).length,
            0,
        );
        return { direct, inferred, total: direct + inferred };
    }, [signalRows]);

    const directPct = Math.round(toNumber(data?.direct_evidence_pct) * 100);
    const inferredPct = Math.round(toNumber(data?.inferred_evidence_pct) * 100);
    const confidencePct = Math.round(toNumber(data?.confidence) * 100);
    const tierColor = TIER_COLORS[data?.tier] || "#3b82f6";
    const underwriting = getUnderwritingView(toNumber(data?.score), toNumber(data?.confidence));

    const handleExportPDF = () => {
        if (!data) return;
        try {
            const doc = new jsPDF();
            const tc = TIER_COLORS[data.tier] || "#334155";
            doc.setFont("helvetica", "bold");
            doc.setFontSize(16);
            doc.text("GenZ Credit Score - Lender Report", 20, 20);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text(`Report ID: ${id} | Generated: ${formatDate(new Date().toISOString())}`, 20, 28);
            doc.setDrawColor(59, 130, 246);
            doc.line(20, 32, 190, 32);

            if (data.name) {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.text(`Applicant: ${data.name}`, 20, 42);
            }
            doc.setFont("helvetica", "bold");
            doc.setFontSize(28);
            doc.text(`${data.score}`, 20, 58);
            doc.setFontSize(11);
            doc.text(`${data.tier} Tier`, 55, 58);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.text(`Confidence: ${confidencePct}% | Model: ${data.model_version || "v1.0"}`, 20, 66);

            let y = 80;
            doc.setFont("helvetica", "bold");
            doc.setFontSize(12);
            doc.text("Signal Breakdown", 20, y);
            y += 8;
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            signalRows.forEach((row) => {
                doc.text(`${row.label}: ${row.score}/100 (${Math.round(row.weight * 100)}% weight)`, 25, y);
                y += 7;
            });

            if (data?.ai_explanation?.lender_summary) {
                y += 6;
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.text("Lender Summary", 20, y);
                y += 8;
                doc.setFont("helvetica", "normal");
                doc.setFontSize(9);
                const lines = doc.splitTextToSize(data.ai_explanation.lender_summary, 165);
                doc.text(lines, 25, y);
                y += lines.length * 4.5 + 5;
            }

            y += 4;
            doc.text(`Direct Evidence: ${directPct}% | Inferred: ${inferredPct}%`, 20, y);
            doc.setFontSize(8);
            doc.setTextColor(128);
            doc.text("CONFIDENTIAL — Alternative underwriting view. Not a bureau score replacement.", 20, 282);
            doc.save(`lender-report-${data.score}-${String(data.tier || "").toLowerCase()}.pdf`);
            toast.success("Lender report PDF exported");
        } catch {
            toast.error("PDF export failed");
        }
    };

    if (loading) {
        return (
            <div className="pt-24 flex justify-center min-h-dvh bg-slate-50" data-testid="lender-report">
                <div className="animate-pulse text-slate-500">Loading lender report...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="pt-24 px-6 flex justify-center min-h-dvh bg-slate-50" data-testid="lender-report">
                <div className="bg-white p-8 rounded-lg border border-slate-200 text-center max-w-md shadow-sm">
                    <AlertCircle size={32} className="text-rose-500 mx-auto mb-4" />
                    <h2 className="text-lg font-semibold text-slate-800 mb-2">Report Not Found</h2>
                    <p className="text-slate-500 text-sm mb-4">{error}</p>
                    <Link to="/genz-credit-scoring" className="text-blue-600 text-sm font-medium">
                        Go back
                    </Link>
                </div>
            </div>
        );
    }

    const ev = data?.evidence_summary || {};
    const ai = data?.ai_explanation || {};
    const persona = data?.persona_match || {};

    return (
        <div className="min-h-dvh bg-slate-50 pt-20" data-testid="lender-report">
            <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 sticky top-16 z-30">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 min-w-0">
                    <div className="min-w-0">
                        <h1 className="text-lg font-bold text-slate-800 font-[Manrope]">GenZ Credit Score — Lender Report</h1>
                        <p className="text-xs text-slate-500 break-words">Report ID: {id} | {formatDate(data?.created_at)}</p>
                    </div>
                    <button
                        onClick={handleExportPDF}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-all inline-flex items-center justify-center gap-2 active:scale-[0.98] w-full sm:w-auto"
                        data-testid="lender-export-pdf"
                    >
                        <Download size={14} /> Export PDF
                    </button>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col lg:flex-row gap-6 overflow-hidden">
                <aside className="hidden lg:block w-72 shrink-0 sticky top-28 self-start">
                    <LenderSimulator baseScore={data?.score} signals={data?.signal_scores} />
                </aside>

                <main className="w-full min-w-0 flex-1 space-y-6">
                    <ScrollReveal>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 min-w-0">
                            <ExecutiveCard
                                title="Score"
                                value={data?.score}
                                subtitle={`${data?.tier || "Tier"} classification`}
                                accent={tierColor}
                                mono
                                testId="lender-exec-score"
                            />
                            <ExecutiveCard
                                title="Confidence"
                                value={confidencePct}
                                suffix="%"
                                subtitle={underwriting.risk}
                                accent="#0f766e"
                                mono
                                testId="lender-exec-confidence"
                            />
                            <ExecutiveCard
                                title="Direct Evidence"
                                value={directPct}
                                suffix="%"
                                subtitle={`${evidenceBreakdown.direct} verified lineage signals`}
                                accent="#15803d"
                                mono
                                testId="lender-exec-direct"
                            />
                            <ExecutiveCard
                                title="Inferred Evidence"
                                value={inferredPct}
                                suffix="%"
                                subtitle={`${evidenceBreakdown.inferred} model-inferred points`}
                                accent="#7c3aed"
                                mono
                                testId="lender-exec-inferred"
                            />
                        </div>
                    </ScrollReveal>

                    <div className="lg:hidden">
                        <LenderSimulator baseScore={data?.score} signals={data?.signal_scores} />
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: DURATION.sectionReveal, ease: EASING.smooth, delay: 0.05 }}
                        className={`rounded-xl border p-5 ${underwriting.cls}`}
                    >
                        <div className="flex items-start gap-3">
                            <underwriting.icon size={18} className="mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-wide">{underwriting.label}</p>
                                <p className="text-sm leading-relaxed mt-1">{underwriting.summary}</p>
                            </div>
                        </div>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Section title="Applicant Summary" icon={Users}>
                            <DataRow label="Applicant" value={data?.name || "Anonymous"} highlight />
                            <DataRow label="Institution" value={ev?.academic?.institution || "Not provided"} />
                            <DataRow label="Course" value={ev?.academic?.course || ev?.academic?.category || "N/A"} />
                            <DataRow label="Year" value={ev?.academic?.year ? `Year ${ev.academic.year}` : "N/A"} />
                            <DataRow label="Model Version" value={data?.model_version || "v1.0"} />
                            <DataRow label="Generated" value={formatDate(data?.created_at)} />
                            {/* The audit trail was already being stored; this is what reads it back. */}
                            <DecisionVerification scoreId={id} isDemo={isDemo} />
                        </Section>

                        <Section title="Persona Context" icon={Building}>
                            <DataRow
                                label="Matched Cohort"
                                value={cohortLabel(persona)}
                                highlight
                            />
                            <DataRow label="Institution Tier" value={titleCase(persona?.institution_tier) || "N/A"} />
                            <DataRow
                                label="Percentile Range"
                                value={Array.isArray(persona?.percentile_range) ? `${persona.percentile_range[0]}–${persona.percentile_range[1]}th` : "N/A"}
                            />
                            <DataRow label="Persona Confidence" value={persona?.confidence ? `${Math.round(persona.confidence * 100)}%` : "N/A"} />
                            <DataRow label="Match Type" value={persona?.is_inferred ? "Inferred" : "Direct"} />
                        </Section>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Section title="Academic Credentials" icon={GraduationCap}>
                            <DataRow label="Institution Tier" value={titleCase(ev?.academic?.tier)} />
                            <DataRow label="CGPA / Score" value={ev?.academic?.cgpa} highlight />
                            <DataRow label="Grade Trend" value={titleCase(ev?.academic?.grade_trend)} />
                            <DataRow label="Attendance" value={ev?.academic?.attendance ? `${ev.academic.attendance}%` : "N/A"} />
                        </Section>

                        <Section title="Fee & Payment Behavior" icon={CreditCard}>
                            <DataRow label="Pattern" value={titleCase(ev?.fee_payment?.pattern)} />
                            <DataRow label="Timing Discipline" value={titleCase(ev?.fee_payment?.timing)} highlight />
                            <DataRow label="Payer Type" value={titleCase(ev?.fee_payment?.payer)} />
                            <DataRow label="Loan Detected" value={ev?.fee_payment?.loan_detected ? "Yes" : "No"} highlight={Boolean(ev?.fee_payment?.loan_detected)} />
                        </Section>
                    </div>

                    <Section title="Digital Footprint" icon={Globe}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                            <div>
                                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Developer Signals</p>
                                <DataRow label="GitHub Repositories" value={ev?.developer?.github_repos} />
                                <DataRow label="GitHub Stars" value={ev?.developer?.github_stars} />
                                <DataRow label="Languages" value={Array.isArray(ev?.developer?.languages) ? ev.developer.languages.join(", ") : "N/A"} />
                                <DataRow label="Recent Activity" value={ev?.developer?.recent_activity ? "Active" : "N/A"} />
                            </div>
                            <div>
                                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Social Signals</p>
                                <DataRow label="YouTube Subscribers" value={ev?.social?.youtube_subs ? Number(ev.social.youtube_subs).toLocaleString("en-IN") : "N/A"} />
                                <DataRow label="Twitter Followers" value={ev?.social?.twitter_followers ? Number(ev.social.twitter_followers).toLocaleString("en-IN") : "N/A"} />
                                <DataRow label="Instagram Followers" value={ev?.social?.instagram_followers ? Number(ev.social.instagram_followers).toLocaleString("en-IN") : "N/A"} />
                                <DataRow label="Reddit Karma" value={ev?.social?.reddit_karma ? Number(ev.social.reddit_karma).toLocaleString("en-IN") : "N/A"} />
                            </div>
                        </div>
                    </Section>

                    <Section title="Signal Breakdown" icon={TrendingUp}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                    <tr>
                                        <th className="pb-2 pr-4">Signal</th>
                                        <th className="pb-2 pr-4">Score</th>
                                        <th className="pb-2 pr-4">Weight</th>
                                        <th className="pb-2 pr-4">Weighted</th>
                                        <th className="pb-2">Sources</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {signalRows.map((row, index) => (
                                        <motion.tr
                                            key={row.key}
                                            initial={{ opacity: 0, y: 6 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.25, ease: EASING.standard, delay: index * 0.03 }}
                                            className="border-b border-slate-100"
                                        >
                                            <td className="py-2.5 pr-4 font-medium text-slate-800">{row.label}</td>
                                            <td className="py-2.5 pr-4 font-mono font-bold" style={{ color: row.color }}>{row.score}/100</td>
                                            <td className="py-2.5 pr-4 text-slate-600">{Math.round(row.weight * 100)}%</td>
                                            <td className="py-2.5 pr-4 font-mono text-slate-700">{row.weightedContribution}</td>
                                            <td className="py-2.5">
                                                <div className="flex flex-wrap gap-1.5">
                                                    {row.evidenceTypes.length > 0 ? row.evidenceTypes.map((type, i) => (
                                                        <span
                                                            key={`${row.key}-${type}-${i}`}
                                                            className={`text-[10px] px-2 py-0.5 rounded-full border ${
                                                                DIRECT_TYPES.has(type)
                                                                    ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                                                    : "bg-violet-50 text-violet-700 border-violet-100"
                                                            }`}
                                                        >
                                                            {SOURCE_LABELS[type] || titleCase(type)}
                                                        </span>
                                                    )) : <span className="text-slate-400 text-xs">No lineage metadata</span>}
                                                </div>
                                            </td>
                                        </motion.tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Section>

                    <Section title="Evidence Lineage" icon={Database}>
                        <div className="space-y-4">
                            <div className="h-3 rounded-full bg-slate-100 overflow-hidden flex">
                                <div className="h-full bg-emerald-500" style={{ width: `${directPct}%` }} />
                                <div className="h-full bg-violet-400" style={{ width: `${inferredPct}%` }} />
                            </div>
                            <div className="flex flex-wrap gap-3 text-xs">
                                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-emerald-100 bg-emerald-50 text-emerald-700">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                    Direct Evidence {directPct}%
                                </div>
                                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-violet-100 bg-violet-50 text-violet-700">
                                    <div className="w-2 h-2 rounded-full bg-violet-400" />
                                    Inferred Evidence {inferredPct}%
                                </div>
                                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-600">
                                    <Layers size={12} />
                                    Lineage Nodes {evidenceBreakdown.total}
                                </div>
                            </div>
                        </div>
                    </Section>

                    {ai?.lender_summary && (
                        <Section title="AI Recommendation" icon={Brain}>
                            <p className="text-sm text-slate-700 leading-relaxed mb-3">{ai.lender_summary}</p>
                            {Array.isArray(ai?.improvement_suggestions) && ai.improvement_suggestions.length > 0 && (
                                <div className="space-y-1.5">
                                    {ai.improvement_suggestions.slice(0, 4).map((item, index) => (
                                        <div key={index} className="text-xs text-slate-600 flex items-start gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Section>
                    )}

                    <p className="text-xs text-slate-500 text-center">
                        CONFIDENTIAL — This alternative assessment supports underwriting decisions and does not replace bureau credit scores.
                    </p>
                </main>
            </div>
        </div>
    );
}
